import React, { useState } from 'react';
import { Icon, Modal, Select, SelectItem } from './ui';
import IDMUserSelect from './IDMUserSelect';
import MCPSearchSelect from './MCPSearchSelect';
import PolicyVersionSelector from './PolicyVersionSelector';
import { IDMUser, createAgent } from '../lib/agentsApi';
import './CreateAgentWizard.css';
import { useTranslation } from 'react-i18next';

interface CreateAgentWizardProps {
  apiUrl: string;
  onClose: () => void;
  onCreated: (result: { agentName: string; ownerName: string; ownerDefaulted: boolean }) => void;
}

const STEPS = ['Basic Info', 'MCP Access', 'Policy', 'Review'] as const;

const CreateAgentWizard: React.FC<CreateAgentWizardProps> = ({ apiUrl, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [role, setRole] = useState('developer');
  const [agentType, setAgentType] = useState<'agent' | 'gateway'>('agent');
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerUser, setOwnerUser] = useState<IDMUser | null>(null);
  const [mcpIds, setMcpIds] = useState<string[]>([]);
  const [policyVersionId, setPolicyVersionId] = useState<string | null>(null);

  const canNext = step === 0 ? name.trim().length > 0 : true;
  const isLast = step === STEPS.length - 1;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await createAgent(apiUrl, {
        name: name.trim(),
        role,
        owner_user_id: ownerId,
        owner_user_name: ownerUser?.name || null,
        mcp_server_ids: mcpIds,
        policy_version_id: policyVersionId,
        description: description.trim() || undefined,
        agent_type: agentType,
      });
      onCreated({
        agentName: result.agent.name,
        ownerName: result.owner.user_name,
        ownerDefaulted: result.owner.defaulted,
      });
    } catch (e: any) {
      setError(e.message || 'Failed to create agent');
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={t('components.createAgentWizard.createAgent')}
      icon="users"
      onClose={onClose}
      maxWidth={640}
      footer={
        <div className="caw-foot">
          <button
            className="ent-btn"
            onClick={step === 0 ? onClose : () => setStep((s) => s - 1)}
            disabled={submitting}
          >
            {step === 0 ? t('components.createAgentWizard.cancel') : t('components.createAgentWizard.back')}
          </button>
          {isLast ? (
            <button className="ent-btn primary" onClick={submit} disabled={submitting || !name.trim()}>
              <Icon name="checkCircle" /> {submitting ? t('components.createAgentWizard.creating') : t('components.createAgentWizard.createAgent')}
            </button>
          ) : (
            <button className="ent-btn primary" onClick={() => setStep((s) => s + 1)} disabled={!canNext}>
              {t('components.createAgentWizard.next')} <Icon name="chevronRight" />
            </button>
          )}
        </div>
      }
    >
      {/* Step indicator */}
      <div className="caw-steps">
        {STEPS.map((label, i) => (
          <div key={label} className={`caw-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span className="caw-step-dot">{i < step ? <Icon name="check" size={12} /> : i + 1}</span>
            <span className="caw-step-label">{label}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className="notice error" style={{ marginBottom: 14 }}>
          <Icon name="warning" /> {error}
        </div>
      )}

      {/* Step 1: Basic Info */}
      {step === 0 && (
        <div className="caw-body">
          <div className="ent-field">
            <label>{t('components.createAgentWizard.agentName')}</label>
            <input
              className="ent-input"
              type="text"
              placeholder={t('components.createAgentWizard.eGBillingAssistant')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="ent-field">
            <label>{t('components.createAgentWizard.description')}</label>
            <input
              className="ent-input"
              type="text"
              placeholder={t('components.createAgentWizard.whatDoesThisAgentDoOptional')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="ent-field">
            <label>{t('components.createAgentWizard.role')}</label>
            <Select value={role} onValueChange={setRole}>
              <SelectItem value="viewer">{t('components.createAgentWizard.viewer')}</SelectItem>
              <SelectItem value="developer">{t('components.createAgentWizard.developer')}</SelectItem>
              <SelectItem value="admin">{t('components.createAgentWizard.admin')}</SelectItem>
            </Select>
          </div>
          <div className="ent-field">
            <label>{t('components.createAgentWizard.type')}</label>
            <Select value={agentType} onValueChange={(v) => setAgentType(v as 'agent' | 'gateway')}>
              <SelectItem value="agent">{t('components.createAgentWizard.agentASingleWorkload')}</SelectItem>
              <SelectItem value="gateway">{t('components.createAgentWizard.gatewayAnExternalPlatform')}</SelectItem>
            </Select>
            {agentType === 'gateway' && (
              <p className="caw-owner-note">
                <Icon name="info" size={12} /> {t('components.createAgentWizard.aGatewayCredentialCarriesTrafficFor')}
              </p>
            )}
          </div>
          <div className="ent-field" style={{ marginBottom: 0 }}>
            <label>{t('components.createAgentWizard.humanOwner')}</label>
            <IDMUserSelect
              apiUrl={apiUrl}
              value={ownerId}
              onChange={(id, user) => { setOwnerId(id); setOwnerUser(user); }}
            />
            {!ownerId && (
              <p className="caw-owner-note">
                <Icon name="info" size={12} /> {t('components.createAgentWizard.noOwnerSelectedADefaultOwner')}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: MCP Access */}
      {step === 1 && (
        <div className="caw-body">
          <p className="muted-text" style={{ marginTop: 0 }}>
            {t('components.createAgentWizard.optionallyGrantThisAgentAccessTo')}
          </p>
          <MCPSearchSelect apiUrl={apiUrl} selected={mcpIds} onChange={setMcpIds} />
        </div>
      )}

      {/* Step 3: Policy */}
      {step === 2 && (
        <div className="caw-body">
          <PolicyVersionSelector apiUrl={apiUrl} value={policyVersionId} onChange={setPolicyVersionId} />
        </div>
      )}

      {/* Step 4: Review */}
      {step === 3 && (
        <div className="caw-body caw-review">
          <div className="caw-review-row">
            <span className="caw-review-key">{t('components.createAgentWizard.name')}</span>
            <span className="caw-review-val">{name || <em>—</em>}</span>
          </div>
          {description && (
            <div className="caw-review-row">
              <span className="caw-review-key">{t('components.createAgentWizard.description')}</span>
              <span className="caw-review-val">{description}</span>
            </div>
          )}
          <div className="caw-review-row">
            <span className="caw-review-key">{t('components.createAgentWizard.role')}</span>
            <span className="caw-review-val"><span className="code-token muted">{role}</span></span>
          </div>
          <div className="caw-review-row">
            <span className="caw-review-key">{t('components.createAgentWizard.type')}</span>
            <span className="caw-review-val">
              {agentType === 'gateway' ? (
                <span className="badge badge-info">{t('components.createAgentWizard.gateway')}</span>
              ) : (
                <span className="code-token muted">{t('components.createAgentWizard.agent')}</span>
              )}
            </span>
          </div>
          <div className="caw-review-row">
            <span className="caw-review-key">{t('components.createAgentWizard.owner')}</span>
            <span className="caw-review-val">
              {ownerUser ? ownerUser.name : <em>{t('components.createAgentWizard.defaultOwnerAutoAssigned')}</em>}
            </span>
          </div>
          <div className="caw-review-row">
            <span className="caw-review-key">{t('components.createAgentWizard.mcpAccess')}</span>
            <span className="caw-review-val">
              {mcpIds.length > 0 ? `${mcpIds.length} server(s)` : <em>{t('components.createAgentWizard.none')}</em>}
            </span>
          </div>
          <div className="caw-review-row">
            <span className="caw-review-key">{t('components.createAgentWizard.policy')}</span>
            <span className="caw-review-val">
              {policyVersionId ? t('components.createAgentWizard.pinnedPolicyVersion') : <em>{t('components.createAgentWizard.inheritPlatformDefault')}</em>}
            </span>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default CreateAgentWizard;
